import L from "leaflet";
import Geohash from "latlon-geohash";
import authService from "./auth.service";

/**
 * Harita üzerindeki imaj katmanlarını yöneten servis
 */
class ImageOverlayService {
  constructor() {
    this.imageLayers = {};
    this.map = null;
    this.isStylesAdded = false;
    this.onImageClick = null; // Tıklama olayını dinleyecek callback
    this.currentPrecision = 0; // Mevcut precision değeri
    this.imageOpacity = 1; // Varsayılan görsel opaklığı
    this.imageRatioCache = {}; // Görüntü en-boy oranlarını önbelleğe alacak
    this.isPhotoUploadMode = false; // Fotoğraf yükleme modu durumu
    this.previousOpacity = 1; // Upload modundan çıkınca geri dönmek için
    this.isRouteSelectionMode = false; // Rota oluşturma modu
    this.onRoutePointSelect = null; // Rota noktası seçildiğinde çağrılacak callback
    this.routePolyline = null; // Rota çizgisi
    this.routeMarkers = []; // Rota numaralı marker'ları + ok marker'ları
    this.highlightedRouteHashes = null; // Vurgulanan rota geohash'leri (Set)
    this.exploreRouteLayers = []; // Keşif modundaki rota çizgileri
    this.onExploreRouteClick = null; // Keşif rotasına tıklanınca çağrılacak callback
    this.currentGeofences = []; // Haritada görünen geofence'ler
    this.onGeofencesChange = null; // Geofence'ler değiştiğinde çağrılacak callback
  }

  /**
   * Servisin kullanacağı haritayı ayarlar
   * @param {Object} map - Leaflet harita objesi
   */
  setMap(map) {
    this.map = map;
    this.addCustomStyles();
  }

  /**
   * Resim tıklama olayı dinleyicisini ayarlar
   * @param {Function} callback - Tıklama olayında çağrılacak fonksiyon
   */
  setImageClickListener(callback) {
    this.onImageClick = callback;
  }

  /**
   * Rota oluşturma modunu ayarlar
   * Aktifken, fotoğraf tıklamaları rota noktası seçimi olarak işlenir
   * @param {Boolean} enabled - Mod aktif mi
   * @param {Function} callback - Nokta seçildiğinde çağrılacak (hash, hashData)
   */
  setRouteSelectionMode(enabled, callback) {
    this.isRouteSelectionMode = enabled;
    this.onRoutePointSelect = callback;
  }

  /**
   * Mevcut precision'ı ayarlar
   * @param {Number} precision - Precision değeri
   */
  setCurrentPrecision(precision) {
    this.currentPrecision = precision;
  }

  /**
   * Görsel opaklığını ayarlar ve tüm görsel katmanlarını yeniler
   * @param {Number} opacity - 0 ile 1 arasında opaklık değeri
   */
  setImageOpacity(opacity) {
    this.imageOpacity = opacity;

    // Mevcut tüm görsel katmanlarının opaklığını güncelle
    if (this.map) {
      Object.values(this.imageLayers).forEach((layer) => {
        if (this.map.hasLayer(layer)) {
          layer.setOpacity(opacity);
        }
      });
    }
  }

  /**
   * Sadece görsel opaklığını günceller, overlay'leri yeniden oluşturmaz
   * @param {Number} opacity - 0 ile 1 arasında opaklık değeri
   */
  updateOpacityOnly(opacity) {
    // Upload modunda değilse opacity'yi sakla
    if (!this.isPhotoUploadMode) {
      this.previousOpacity = opacity;
    }

    this.imageOpacity = opacity;

    // Mevcut tüm görsel katmanlarının opaklığını güncelle, yeniden oluşturmadan
    if (this.map) {
      Object.values(this.imageLayers).forEach((layer) => {
        if (this.map.hasLayer(layer)) {
          layer.setOpacity(opacity);
        }
      });
    }
  }

  /**
   * Fotoğraf yükleme modunu ayarlar
   * @param {Boolean} isUploadMode - Yükleme modu durumu
   */
  setPhotoUploadMode(isUploadMode) {
    const wasUploadMode = this.isPhotoUploadMode;
    this.isPhotoUploadMode = isUploadMode;

    if (isUploadMode && !wasUploadMode) {
      // Upload moduna girerken opacity'yi minimum yap
      this.previousOpacity = this.imageOpacity;
      this.updateOpacityOnly(0.3);

      // Tüm image'ların interactive özelliğini pointer-events ile devre dışı bırak
      Object.entries(this.imageLayers).forEach(([hash, layer]) => {
        if (this.map && this.map.hasLayer(layer)) {
          const element = layer.getElement();
          if (element) {
            element.style.pointerEvents = "none";
            element.style.cursor = "default";
          }
        }
      });
    } else if (!isUploadMode && wasUploadMode) {
      // Upload modundan çıkarken opacity'yi geri yükle
      this.updateOpacityOnly(this.previousOpacity);

      // Tüm image'ların interactive özelliğini geri aç
      Object.entries(this.imageLayers).forEach(([hash, layer]) => {
        if (this.map && this.map.hasLayer(layer)) {
          const element = layer.getElement();
          if (element) {
            element.style.pointerEvents = "auto";
            element.style.cursor = "pointer";
          }
        }
      });
    }
  }

  /**
   * Özel CSS stillerini ekler
   */
  addCustomStyles() {
    if (this.isStylesAdded) return;

    const styleId = "geohash-image-styles";
    // Eğer stil zaten eklenmişse tekrar ekleme
    if (document.getElementById(styleId)) {
      this.isStylesAdded = true;
      return;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .geohash-image {
        position: fixed;
        filter: contrast(1.1) saturate(1.2);
        box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
        border: 0px solid white;
        border-radius: 25%;
        object-fit: cover !important; /* Orijinal en-boy oranını korurken kutuyu doldurur */
      }
      .geohash-image-user-owned {
        box-shadow: 0 0 30px rgba(103, 73, 2, 0.5);
      }
      .geohash-image:hover {
        filter: contrast(1.2) saturate(1.3) brightness(1.1);
        transform: scale(1.02);
        z-index: 1000 !important;
      }
    `;
    document.head.appendChild(style);
    this.isStylesAdded = true;
  }

  /**
   * Tüm imaj katmanlarını temizler
   */
  clearAllImageLayers() {
    if (!this.map) return;

    Object.values(this.imageLayers).forEach((layer) => {
      if (this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    });
    this.imageLayers = {};
  }

  /**
   * Görüntünün orijinal en-boy oranını alır ve bu orana göre haritada konumlandırmasını yapar
   * @param {String} url - Görüntünün URL'si
   * @param {Array} bounds - Geohash sınırları
   * @param {Function} callback - İşlem tamamlandığında çağrılacak fonksiyon
   */
  getImageRatioAndCreateOverlay(
    url,
    bounds,
    hashData,
    item,
    options,
    callback,
  ) {
    // Daha önce hesaplanmış oranı kullan (önbellek)
    if (this.imageRatioCache[url]) {
      callback(this.imageRatioCache[url]);
      return;
    }

    // Yeni görüntü oluştur ve yüklendiğinde oranı hesapla
    const img = new Image();
    img.onload = () => {
      const imageRatio = img.width / img.height;
      this.imageRatioCache[url] = imageRatio;
      callback(imageRatio);
    };
    img.onerror = () => {
      // Hata durumunda varsayılan bir oran kullan
      callback(1); // 1:1 oran (kare)
    };
    img.src = url;
  }

  /**
   * Geohash sınırlarını görüntü oranına göre ayarlar
   * @param {Array} bounds - Orijinal geohash sınırları
   * @param {Number} imageRatio - Görüntünün en-boy oranı
   * @returns {Array} - Ayarlanmış sınırlar
   */
  adjustBoundsToImageRatio(bounds, imageRatio) {
    // bounds: [[sw.lat, sw.lon], [ne.lat, ne.lon]]
    const sw = L.latLng(bounds[0][0], bounds[0][1]);
    const ne = L.latLng(bounds[1][0], bounds[1][1]);

    // Geohash'in sınır genişliği ve yüksekliği
    const latLngBounds = L.latLngBounds(sw, ne);
    const center = latLngBounds.getCenter();
    const width = ne.lng - sw.lng;
    const height = ne.lat - sw.lat;
    const geohashRatio = width / height;

    let newBounds;

    if (imageRatio > geohashRatio) {
      // Görüntü daha yatay, yüksekliği azalt
      const newHeight = width / imageRatio;
      const newSouthLat = center.lat - newHeight / 2;
      const newNorthLat = center.lat + newHeight / 2;
      newBounds = [
        [newSouthLat, sw.lng],
        [newNorthLat, ne.lng],
      ];
    } else {
      // Görüntü daha dikey, genişliği azalt
      const newWidth = height * imageRatio;
      const newWestLng = center.lng - newWidth / 2;
      const newEastLng = center.lng + newWidth / 2;
      newBounds = [
        [sw.lat, newWestLng],
        [ne.lat, newEastLng],
      ];
    }

    return newBounds;
  }

  /**
   * Rota noktalarını haritada vurgular:
   * - Rota dışındaki fotoğrafların opacity'sini düşürür
   * - Rota noktaları arasına kesikli çizgi + ok çizer
   * - Numaralı marker'lar ekler
   * - Haritayı rota sınırlarına sığdırır
   * @param {Array} routePoints - Rota noktaları [{geohash, image_url, order}, ...]
   */
  highlightRoute(routePoints) {
    this.clearRouteHighlight();
    if (!this.map || !routePoints || routePoints.length < 2) return;

    this._addRouteStyles();

    const sortedPoints = [...routePoints].sort((a, b) => a.order - b.order);
    const routeHashes = new Set(sortedPoints.map((p) => p.geohash));
    this.highlightedRouteHashes = routeHashes;

    // Rota dışı fotoğrafları soldur
    Object.entries(this.imageLayers).forEach(([hash, layer]) => {
      if (this.map.hasLayer(layer)) {
        if (routeHashes.has(hash)) {
          layer.setOpacity(1);
          layer.setZIndex(1000);
        } else {
          layer.setOpacity(0);
          layer.setZIndex(500);
        }
      }
    });

    // Geohash'leri lat/lng'ye çevir
    const latLngs = sortedPoints.map((p) => {
      const decoded = Geohash.decode(p.geohash);
      return [decoded.lat, decoded.lon];
    });

    // Arka plan glow çizgisi (geniş, yarı saydam)
    const glowLine = L.polyline(latLngs, {
      color: "#4caf50",
      weight: 10,
      opacity: 0.15,
      lineJoin: "round",
      lineCap: "round",
      className: "route-glow-line",
    }).addTo(this.map);
    this.routeMarkers.push(glowLine);

    // Ana çizgi (animated dash)
    this.routePolyline = L.polyline(latLngs, {
      color: "#4caf50",
      weight: 3.5,
      opacity: 0.9,
      dashArray: "8, 12",
      lineJoin: "round",
      lineCap: "round",
      className: "route-main-line",
    }).addTo(this.map);

    // Numaralı marker'lar
    const isStart = (i) => i === 0;
    const isEnd = (i) => i === sortedPoints.length - 1;

    sortedPoints.forEach((p, i) => {
      const decoded = Geohash.decode(p.geohash);
      const size = isStart(i) || isEnd(i) ? 34 : 28;
      const bg = isStart(i) ? "#4caf50" : isEnd(i) ? "#f44336" : "#fff";
      const fg = isStart(i) || isEnd(i) ? "#fff" : "#4caf50";
      const border =
        isStart(i) || isEnd(i) ? "3px solid #fff" : "2.5px solid #4caf50";
      const shadow = isStart(i)
        ? "0 3px 12px rgba(76,175,80,0.6)"
        : isEnd(i)
          ? "0 3px 12px rgba(244,67,54,0.6)"
          : "0 2px 8px rgba(0,0,0,0.2)";
      const label = isStart(i) ? "▶" : isEnd(i) ? "⬤" : i + 1;
      const fontSize = isStart(i) || isEnd(i) ? "14px" : "13px";
      const animClass = isStart(i)
        ? "route-marker-start"
        : isEnd(i)
          ? "route-marker-end"
          : "route-marker-mid";

      const marker = L.marker([decoded.lat, decoded.lon], {
        icon: L.divIcon({
          className: `route-point-marker ${animClass}`,
          html: `<div style="
            width:${size}px;height:${size}px;background:${bg};color:${fg};
            border-radius:50%;display:flex;align-items:center;justify-content:center;
            font-weight:700;font-size:${fontSize};border:${border};
            box-shadow:${shadow};transition:transform 0.3s ease;
          ">${label}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        }),
        interactive: false,
        zIndexOffset: isStart(i) ? 2000 : isEnd(i) ? 1900 : 1500,
      }).addTo(this.map);
      this.routeMarkers.push(marker);
    });

    // Yön okları (segment ortalarında, SVG chevron)
    for (let i = 0; i < latLngs.length - 1; i++) {
      const from = latLngs[i];
      const to = latLngs[i + 1];
      const midLat = (from[0] + to[0]) / 2;
      const midLng = (from[1] + to[1]) / 2;
      const dx = to[1] - from[1];
      const dy = to[0] - from[0];
      const angle = Math.atan2(dx, dy) * (180 / Math.PI);

      const arrow = L.marker([midLat, midLng], {
        icon: L.divIcon({
          className: "route-arrow-marker",
          html: `<div style="
            width:24px;height:24px;display:flex;align-items:center;justify-content:center;
            transform:rotate(${angle}deg);
          ">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 4L20 12L12 20" stroke="#4caf50" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              <path d="M4 12H20" stroke="#4caf50" stroke-width="3.5" stroke-linecap="round"/>
            </svg>
          </div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
        interactive: false,
        zIndexOffset: 1200,
      }).addTo(this.map);
      this.routeMarkers.push(arrow);
    }

    // Haritayı rota sınırlarına sığdır
    this.map.fitBounds(this.routePolyline.getBounds(), {
      padding: [60, 60],
      maxZoom: 15,
      animate: true,
    });
  }

  /**
   * Rota çizim stilleri (bir kez eklenir)
   */
  _addRouteStyles() {
    const styleId = "route-highlight-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .route-main-line {
        animation: routeDash 25s linear infinite;
      }
      @keyframes routeDash {
        to { stroke-dashoffset: -500; }
      }
      .route-glow-line {
        filter: blur(2px);
      }
      .route-marker-start > div {
        animation: routeMarkerPulse 2s ease-in-out infinite;
      }
      .route-marker-end > div {
        animation: routeMarkerPulse 2s ease-in-out infinite 0.5s;
      }
      .route-marker-mid > div:hover {
        transform: scale(1.15);
      }
      @keyframes routeMarkerPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      .route-arrow-marker {
        opacity: 0.7;
      }
      .route-point-marker {
        background: none !important;
        border: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Rota vurgusunu temizler - tüm çizgi/marker'ları kaldırır, opacity'leri geri yükler
   */
  clearRouteHighlight() {
    if (this.routePolyline && this.map) {
      this.map.removeLayer(this.routePolyline);
      this.routePolyline = null;
    }

    this.routeMarkers.forEach((m) => {
      if (this.map && this.map.hasLayer(m)) {
        this.map.removeLayer(m);
      }
    });
    this.routeMarkers = [];

    // Fotoğraf opacity'lerini geri yükle
    if (this.highlightedRouteHashes) {
      this.highlightedRouteHashes = null;
      Object.values(this.imageLayers).forEach((layer) => {
        if (this.map && this.map.hasLayer(layer)) {
          layer.setOpacity(this.imageOpacity);
          layer.setZIndex(600);
        }
      });
    }
  }

  /**
   * Mevcut geofence'leri günceller ve değişiklik callback'ini çağırır
   * @param {Array<string>} geofences
   */
  setCurrentGeofences(geofences) {
    this.currentGeofences = geofences || [];
    if (this.onGeofencesChange) {
      this.onGeofencesChange(this.currentGeofences);
    }
  }

  /**
   * Geofence değişiklik dinleyicisini ayarlar
   * @param {Function} callback - (geofences) => void
   */
  setGeofencesChangeListener(callback) {
    this.onGeofencesChange = callback;
  }

  /**
   * Keşif rotası tıklama callback'ini ayarlar
   * @param {Function} callback - (routeId) => void
   */
  setExploreRouteClickListener(callback) {
    this.onExploreRouteClick = callback;
  }

  /**
   * Haritada keşif rotalarını çizer - highlightRoute ile aynı stil, tıklanabilir
   * Fotoğraf opacity'lerini değiştirmez, fitBounds yapmaz
   * @param {Array} routes - exploreRoutes API'den dönen rota dizisi
   */
  drawExploreRoutes(routes) {
    this.clearExploreRoutes();
    if (!this.map || !routes || routes.length === 0) return;

    this._addRouteStyles();

    // Rota çizgileri fotoğrafların üstünde olsun (pane z-index: 650)
    if (!this.map.getPane("exploreRouteLines")) {
      this.map.createPane("exploreRouteLines").style.zIndex = 650;
    }

    const COLORS = [
      "#4caf50",
      "#2196f3",
      "#ff9800",
      "#e91e63",
      "#9c27b0",
      "#00bcd4",
    ];

    // Tüm rota noktalarının fotoğraflarını haritaya ekle (aynı geohash'i tekrar ekleme)
    const addedPhotoHashes = new Set();
    routes.forEach((route) => {
      if (!route.points) return;
      route.points.forEach((p) => {
        if (!p.image_url || !p.geohash || addedPhotoHashes.has(p.geohash))
          return;
        addedPhotoHashes.add(p.geohash);

        const hashBounds = Geohash.bounds(p.geohash);
        const bounds = [
          [hashBounds.sw.lat, hashBounds.sw.lon],
          [hashBounds.ne.lat, hashBounds.ne.lon],
        ];

        const overlay = L.imageOverlay(p.image_url, bounds, {
          opacity: 1,
          interactive: false,
          className: `geohash-image geohash-image-${p.geohash}`,
          zIndex: 400,
        }).addTo(this.map);
        this.exploreRouteLayers.push(overlay);
      });
    });

    routes.forEach((route, idx) => {
      if (!route.points || route.points.length < 2) return;

      const sorted = [...route.points].sort((a, b) => a.order - b.order);
      const latLngs = sorted.map((p) => {
        const decoded = Geohash.decode(p.geohash);
        return [decoded.lat, decoded.lon];
      });

      const color = COLORS[idx % COLORS.length];

      const handleClick = () => {
        if (this.onExploreRouteClick) {
          this.onExploreRouteClick(route.id);
        }
      };

      // Glow çizgisi (pane sayesinde fotoğrafların üstünde)
      const glowLine = L.polyline(latLngs, {
        color,
        weight: 10,
        opacity: 0.15,
        lineJoin: "round",
        lineCap: "round",
        interactive: true,
        className: "route-glow-line",
        pane: "exploreRouteLines",
      }).addTo(this.map);
      glowLine.on("click", handleClick);
      this.exploreRouteLayers.push(glowLine);

      // Ana çizgi (animated dash)
      const mainLine = L.polyline(latLngs, {
        color,
        weight: 3.5,
        opacity: 0.9,
        dashArray: "8, 12",
        lineJoin: "round",
        lineCap: "round",
        interactive: true,
        className: "route-main-line",
        pane: "exploreRouteLines",
      }).addTo(this.map);
      mainLine.on("click", handleClick);
      this.exploreRouteLayers.push(mainLine);
    });
  }

  /**
   * Keşif rota çizgilerini temizler
   */
  clearExploreRoutes() {
    this.exploreRouteLayers.forEach((layer) => {
      if (this.map && this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    });
    this.exploreRouteLayers = [];
  }

  /**
   * Geohash'lere göre imaj katmanlarını ekler
   * @param {Array} geohashes - Geohash nesneleri dizisi
   * @param {Object} data - Geohash'lere ait görsel verileri içeren nesne
   */
  addImageOverlays(geohashes, data) {
    if (!this.map) return;

    // Önce mevcut katmanları temizle
    this.clearAllImageLayers();

    // Mevcut kullanıcı bilgisini al
    const currentUser = authService.getCurrentUser();
    const currentUsername = currentUser ? currentUser.username : null;

    // Her bir geohash için kontrol et
    geohashes.forEach((item) => {
      const hashData = data[item.hash];

      // Eğer bu geohash için veri varsa
      if (hashData && hashData.url) {
        // Kullanıcının kendi fotoğrafı mı kontrol et
        const isUserPhoto =
          currentUsername && hashData.user === currentUsername;

        // CSS sınıfını oluştur
        const className = `geohash-image geohash-image-${item.hash}${
          isUserPhoto ? " geohash-image-user-owned" : ""
        }`;

        // Direkt olarak geohash sınırlarını kullanarak overlay oluştur
        // Görüntünün taşan kısımları CSS ile kesilecek (object-fit: cover)
        const imageOverlay = L.imageOverlay(hashData.url, item.bounds, {
          opacity: this.imageOpacity,
          interactive: !this.isPhotoUploadMode, // Upload modunda tıklanamaz
          className: className,
          zIndex: isUserPhoto ? 650 : 600, // Kullanıcı fotoğraflarını biraz daha önde göster
        });

        // Tıklama olayı ekle
        imageOverlay.on("click", () => {
          // Rota oluşturma modunda ise noktayı seç
          if (this.isRouteSelectionMode && this.onRoutePointSelect) {
            if (this.currentPrecision !== 7) {
              this.onRoutePointSelect(null, null, true);
              return;
            }
            this.onRoutePointSelect(item.hash, hashData, false);
            return;
          }

          // Precision 7'de panel aç
          if (this.currentPrecision === 7) {
            // Kullanıcı giriş yapmış mı kontrol et
            if (this.onImageClick) {
              this.onImageClick(item.hash, hashData);
            }
          } else {
            // Diğer precision'larda o bölgeye zoom yap
            const newZoom = Math.min(this.map.getZoom() + 2, 18);
            const center = L.latLngBounds(item.bounds).getCenter();

            // Animasyonlu zoom
            this.map.flyTo(center, newZoom, {
              animate: true,
              duration: 0.75,
              easeLinearity: 0.25,
            });

            // Görsel efekt - parlaklığı artır sonra normale dön
            imageOverlay.setOpacity(1);
            imageOverlay.setZIndex(1000);

            // 1.5 saniye sonra opaklığı normale döndür
            setTimeout(() => {
              if (this.imageLayers[item.hash]) {
                this.imageLayers[item.hash].setOpacity(this.imageOpacity);
                this.imageLayers[item.hash].setZIndex(isUserPhoto ? 650 : 600);
              }
            }, 1500);
          }
        });

        // Haritaya ekle
        imageOverlay.addTo(this.map);

        // Referansını sakla (daha sonra temizlemek için)
        this.imageLayers[item.hash] = imageOverlay;

        // Rota vurgusu aktifse, yeni eklenen image'ı da uygula
        if (this.highlightedRouteHashes) {
          if (this.highlightedRouteHashes.has(item.hash)) {
            imageOverlay.setOpacity(1);
            imageOverlay.setZIndex(1000);
          } else {
            imageOverlay.setOpacity(0);
            imageOverlay.setZIndex(500);
          }
        }
      }
    });
  }
}

// Singleton servis instance'ı oluştur
const imageOverlayService = new ImageOverlayService();

export default imageOverlayService;
